import React, { useEffect, useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Box,
  Typography,
  Button,
  IconButton,
  TextField,
  Divider,
  Alert,
} from '@mui/material';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { X, Calendar, Settings2 } from 'lucide-react';
import dayjs, { Dayjs } from 'dayjs';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { useAppSelector } from '@/store';
import { selectCurrentUser } from '@/store/slices/authSlice';
import { enumMasterService, leaveService } from '@/services/api';
import { QUERY_KEYS } from '@/services/queryClient';
import { AppSelect, AppMenuItem } from '@/shared/components/AppSelect';
import { isAdminOrManager } from '@/shared/utils/roles';
import ManageLeaveTypesModal from '@/features/settings/components/ManageLeaveTypesModal';

interface LeaveRequestModalProps {
  open: boolean;
  onClose: () => void;
}

/**
 * LeaveRequestModal
 * Form for requesting time off / leave
 */
const LeaveRequestModal: React.FC<LeaveRequestModalProps> = ({ open, onClose }) => {
  const user = useAppSelector(selectCurrentUser);
  const queryClient = useQueryClient();
  const canManageLeaveTypes = isAdminOrManager(user?.role);

  const [leaveType, setLeaveType] = useState('');
  const [startDate, setStartDate] = useState<Dayjs | null>(dayjs());
  const [endDate, setEndDate] = useState<Dayjs | null>(dayjs().add(1, 'day'));
  const [reason, setReason] = useState('');
  const [manageLeaveTypesOpen, setManageLeaveTypesOpen] = useState(false);

  const { data: leaveTypes = [], isLoading: leaveTypesLoading } = useQuery({
    queryKey: QUERY_KEYS.leaveTypes(),
    queryFn: () => enumMasterService.listLeaveTypes(),
    enabled: open,
    staleTime: 60_000,
  });

  useEffect(() => {
    if (!open || leaveTypes.length === 0) return;
    if (!leaveTypes.some((item) => item.keyValue === leaveType)) {
      setLeaveType(leaveTypes[0].keyValue);
    }
  }, [open, leaveTypes, leaveType]);

  const createLeaveMutation = useMutation({
    mutationFn: (data: { type: string; startDate: string; endDate: string; reason: string; delegateTo?: string }) =>
      leaveService.createLeaveRequest(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: QUERY_KEYS.myLeaves });
      queryClient.invalidateQueries({ queryKey: QUERY_KEYS.calendarEvents });
      queryClient.invalidateQueries({ queryKey: QUERY_KEYS.pendingApprovals });
      queryClient.invalidateQueries({ queryKey: QUERY_KEYS.leaveBalance });
      handleClose();
    },
  });

  const handleSubmit = async () => {
    if (!user || !startDate || !endDate || !leaveType) return;
    createLeaveMutation.mutate({
      type: leaveType,
      startDate: startDate.toISOString(),
      endDate: endDate.toISOString(),
      reason: reason.trim(),
    });
  };

  const handleClose = () => {
    setLeaveType(leaveTypes[0]?.keyValue ?? '');
    setStartDate(dayjs());
    setEndDate(dayjs().add(1, 'day'));
    setReason('');
    setManageLeaveTypesOpen(false);
    onClose();
  };

  return (
    <LocalizationProvider dateAdapter={AdapterDayjs}>
      <Dialog
        open={open}
        onClose={handleClose}
        maxWidth="sm"
        fullWidth
        PaperProps={{ sx: { borderRadius: 2 } }}
      >
        <DialogTitle>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Calendar size={20} />
              <Typography variant="h6" fontWeight={600}>
                Request Leave
              </Typography>
            </Box>
            <IconButton onClick={handleClose} size="small">
              <X size={20} />
            </IconButton>
          </Box>
        </DialogTitle>

        <Divider />

        <DialogContent>
          {createLeaveMutation.isSuccess ? (
            <Alert severity="success" sx={{ mt: 2 }}>
              Leave request submitted successfully! Your manager will review it shortly.
            </Alert>
          ) : (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3, mt: 1 }}>
              <Box>
                <Box sx={{ display: 'flex', gap: 1, alignItems: 'flex-start' }}>
                  <AppSelect
                    label="Leave Type"
                    value={leaveType}
                    onChange={(e) => setLeaveType(e.target.value as string)}
                    disabled={leaveTypesLoading || leaveTypes.length === 0}
                  >
                    {leaveTypes.map((type) => (
                      <AppMenuItem key={type.id} value={type.keyValue}>
                        {type.displayLabel}
                      </AppMenuItem>
                    ))}
                  </AppSelect>
                  {canManageLeaveTypes && (
                    <Button
                      variant="outlined"
                      size="small"
                      startIcon={<Settings2 size={14} />}
                      onClick={() => setManageLeaveTypesOpen(true)}
                      sx={{ mt: 0.5, whiteSpace: 'nowrap', minWidth: 'auto', px: 1.5 }}
                    >
                      Manage
                    </Button>
                  )}
                </Box>
                {!leaveTypesLoading && leaveTypes.length === 0 && (
                  <Alert severity="warning" sx={{ mt: 1 }}>
                    No leave types are configured. Ask an admin to add them in Settings.
                  </Alert>
                )}
              </Box>

              {/* Date Range */}
              <Box sx={{ display: 'flex', gap: 2 }}>
                <DatePicker
                  label="Start Date"
                  value={startDate}
                  onChange={setStartDate}
                  slotProps={{
                    textField: { fullWidth: true, size: 'medium' },
                  }}
                />
                <DatePicker
                  label="End Date"
                  value={endDate}
                  onChange={setEndDate}
                  minDate={startDate || undefined}
                  slotProps={{
                    textField: { fullWidth: true, size: 'medium' },
                  }}
                />
              </Box>

              {/* Reason */}
              <TextField
                label="Reason (Optional)"
                value={reason}
                onChange={(e) => setReason(e.target.value)}
                multiline
                rows={3}
                fullWidth
                placeholder="Briefly describe the reason for your leave request..."
              />

              {/* Info */}
              <Alert severity="info">
                Your request will be sent to your direct manager for approval.
                You'll be notified once a decision is made.
              </Alert>

              {createLeaveMutation.isError && (
                <Alert severity="error">
                  {(createLeaveMutation.error as Error)?.message || 'Failed to submit leave request. Please try again.'}
                </Alert>
              )}
            </Box>
          )}
        </DialogContent>

        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={handleClose} disabled={createLeaveMutation.isPending}>
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={handleSubmit}
            disabled={
              createLeaveMutation.isPending
              || createLeaveMutation.isSuccess
              || !startDate
              || !endDate
              || !leaveType
            }
          >
            {createLeaveMutation.isPending ? 'Submitting...' : 'Submit Request'}
          </Button>
        </DialogActions>
      </Dialog>
      <ManageLeaveTypesModal
        open={manageLeaveTypesOpen}
        onClose={() => setManageLeaveTypesOpen(false)}
        onLeaveTypeAdded={setLeaveType}
      />
    </LocalizationProvider>
  );
};

export default LeaveRequestModal;
